import React, { useState, useMemo, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { CustomModal } from '../../components/ui/CustomModal';
import { formatCurrency, formatNumber, parseFormattedNumber, getLocalDateString, formatDateForInput, calculateItemCost } from '../../utils/helpers';

const initialOrderState = {
    id: '', customerName: '', phone: '', date: getLocalDateString(), source: 'Oreka',
    items: [{ productName: '', quantity: 1, price: 0, purchasePrice: 0, discount: 0, cost: 0 }],
    shippingFee: 0, realShippingFee: 0, promotion: 0, platformFee: 0, label: 'Không có nhãn', status: 'Mới',
    packingWarehouse: 'Kho HCM'
};

export const OrderFormModal = ({ isOpen, onClose, onSave, existingOrder }) => {
    const [order, setOrder] = useState(initialOrderState);
    const warehouseOptions = ["Kho DL", "Kho HCM", "Kho khác"];

    // *** FIX: Logic tính phí Oreka được cập nhật ***
    useEffect(() => {
        // Luôn luôn tính toán lại phí nếu nguồn là Oreka
        if (order.source === 'Oreka') {
            const itemsTotal = order.items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0);
            const orekaFee = Math.round(itemsTotal * 0.114); // 9.9% + 1.5%
            setOrder(p => ({ ...p, platformFee: orekaFee }));
        }
    }, [order.items, order.source]); // Chỉ phụ thuộc vào sản phẩm và nguồn

    useEffect(() => {
        if (isOpen) {
            if (existingOrder) {
                setOrder({
                    ...existingOrder,
                    packingWarehouse: existingOrder.packingWarehouse || 'Kho HCM', 
                    date: formatDateForInput(existingOrder.date),
                    items: existingOrder.items.map(item => {
                        const hasNewFields = item.purchasePrice !== undefined;
                        return {
                            ...item,
                            purchasePrice: hasNewFields ? (item.purchasePrice || 0) : (item.cost || 0),
                            discount: hasNewFields ? String(item.discount || 0) : '0',
                        };
                    })
                });
            } else {
                setOrder({...initialOrderState, date: getLocalDateString()});
            }
        }
    }, [existingOrder, isOpen]);

    const handleItemChange = (index, field, value) => {
        const newItems = [...order.items];
        const currentItem = { ...newItems[index] };

        if (field === 'discount') {
            currentItem[field] = value.replace(/[^0-9.]/g, '');
        } else if (['price', 'purchasePrice', 'quantity'].includes(field)) {
            currentItem[field] = parseFormattedNumber(value);
        } else {
            currentItem[field] = value;
        }

        const tempItemForCalc = {...currentItem, discount: parseFloat(currentItem.discount) || 0};
        currentItem.cost = calculateItemCost(tempItemForCalc);

        newItems[index] = currentItem;
        setOrder(p => ({ ...p, items: newItems }));
    };

    const handleOrderFieldChange = (e) => {
        const { name, value } = e.target;
        const numValue = ['shippingFee', 'realShippingFee', 'promotion', 'platformFee'].includes(name)
            ? parseFormattedNumber(value)
            : value;
        setOrder(p => ({ ...p, [name]: numValue }));
    };
    
    const addItem = () => setOrder(p => ({ ...p, items: [...p.items, { productName: '', quantity: 1, price: 0, purchasePrice: 0, discount: '0', cost: 0 }] }));
    const removeItem = (index) => setOrder(p => ({ ...p, items: p.items.filter((_, i) => i !== index) }));
    
    const handleSubmit = (e) => { 
        e.preventDefault(); 
        const finalOrder = {
            ...order,
            items: order.items.map(item => ({
                ...item,
                discount: parseFloat(item.discount) || 0,
            }))
        };
        onSave(finalOrder);
    };
    
    const totalAmount = useMemo(() => {
        if (!Array.isArray(order.items)) return 0;
        const itemsTotal = order.items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0);
        return itemsTotal + (order.shippingFee || 0) - (order.promotion || 0);
    }, [order.items, order.shippingFee, order.promotion]);

    return (
        <CustomModal isOpen={isOpen} onClose={onClose} title={existingOrder ? "Sửa Đơn Hàng" : "Tạo Đơn Hàng Mới"} maxWidth="max-w-4xl">
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input type="text" name="id" placeholder="Mã đơn hàng" value={order.id} onChange={handleOrderFieldChange} className="p-3 border rounded-md" required/>
                    <input type="text" name="customerName" placeholder="Tên khách hàng" value={order.customerName} onChange={handleOrderFieldChange} className="p-3 border rounded-md" required />
                    <input type="text" name="phone" placeholder="Số điện thoại" value={order.phone} onChange={handleOrderFieldChange} className="p-3 border rounded-md"/>
                    <input type="date" name="date" value={order.date} onChange={handleOrderFieldChange} className="p-3 border rounded-md"/>
                    <select name="source" value={order.source} onChange={handleOrderFieldChange} className="p-3 border rounded-md bg-white">
                        {['Trực tiếp', 'Website', 'Lazada', 'Oreka'].map(s => <option key={s}>{s}</option>)}
                    </select>
                    <select name="label" value={order.label} onChange={handleOrderFieldChange} className="p-3 border rounded-md bg-white">
                        <option>Không có nhãn</option>
                        <option>Góp vốn hàng (Daklak)</option>
                        <option>Góp vốn hàng (HCM)</option>
                    </select>
                    <select name="packingWarehouse" value={order.packingWarehouse} onChange={handleOrderFieldChange} className="p-3 border rounded-md bg-white">
                        {warehouseOptions.map(wh => <option key={wh} value={wh}>{wh}</option>)}
                    </select>
                </div>
                
                <div className="border-t pt-4 space-y-3">
                    <h4 className="text-md font-semibold mb-2">Chi tiết sản phẩm</h4>
                    <div className="grid grid-cols-12 gap-2 px-2 text-sm font-medium text-gray-500">
                        <div className="col-span-4">Tên sản phẩm</div>
                        <div className="col-span-2">Giá mua</div>
                        <div className="col-span-1 text-center">CK (%)</div>
                        <div className="col-span-2">Giá bán</div>
                        <div className="col-span-1 text-center">SL</div>
                        <div className="col-span-2 text-right">Thành tiền</div>
                    </div>
                    {order.items.map((item, index) => (
                        <div key={index} className="grid grid-cols-12 gap-2 items-center">
                            <input type="text" placeholder="Tên sản phẩm" value={item.productName} onChange={(e) => handleItemChange(index, 'productName', e.target.value)} className="p-2 border rounded-md col-span-4"/>
                            <input type="text" placeholder="Giá mua" value={formatNumber(item.purchasePrice)} onChange={(e) => handleItemChange(index, 'purchasePrice', e.target.value)} className="p-2 border rounded-md col-span-2"/>
                            <input type="text" placeholder="CK" value={item.discount} onChange={(e) => handleItemChange(index, 'discount', e.target.value)} className="p-2 border rounded-md col-span-1 text-center"/>
                            <input type="text" placeholder="Giá bán" value={formatNumber(item.price)} onChange={(e) => handleItemChange(index, 'price', e.target.value)} className="p-2 border rounded-md col-span-2"/>
                            <input type="text" placeholder="SL" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} className="p-2 border rounded-md col-span-1 text-center"/>
                            <div className="p-2 border rounded-md col-span-2 bg-gray-100 text-right">{formatCurrency((item.price || 0) * (item.quantity || 0))}</div>
                            <button type="button" onClick={() => removeItem(index)} className="text-red-500 hover:text-red-700 col-span-12 md:col-span-1 flex justify-center"><Trash2 size={18}/></button>
                        </div>
                    ))}
                    <button type="button" onClick={addItem} className="text-sm text-blue-600 hover:text-blue-800 font-semibold">+ Thêm sản phẩm</button>
                </div>
                 <div className="grid grid-cols-1 md:grid-cols-4 gap-4 border-t pt-4">
                      <div>
                          <label className="text-sm font-medium text-gray-700">Phí ship báo khách</label>
                          <input type="text" name="shippingFee" value={formatNumber(order.shippingFee)} onChange={handleOrderFieldChange} className="mt-1 w-full p-3 border rounded-md"/>
                      </div>
                      <div>
                          <label className="text-sm font-medium text-gray-700">Phí ship thực trả</label>
                          <input type="text" name="realShippingFee" value={formatNumber(order.realShippingFee)} onChange={handleOrderFieldChange} className="mt-1 w-full p-3 border rounded-md"/>
                      </div>
                      <div>
                          <label htmlFor="promotion" className="text-sm font-medium text-gray-700">Khuyến mãi</label>
                          <input id="promotion" type="text" name="promotion" value={formatNumber(order.promotion)} onChange={handleOrderFieldChange} className="mt-1 w-full p-3 border rounded-md"/>
                      </div>
                      <div>
                          <label htmlFor="platformFee" className="text-sm font-medium text-gray-700">Phí sàn TMĐT</label>
                          <input id="platformFee" type="text" name="platformFee" value={formatNumber(order.platformFee)} onChange={handleOrderFieldChange} className={`mt-1 w-full p-3 border rounded-md ${order.source === 'Oreka' ? 'bg-gray-100' : ''}`} disabled={order.source === 'Oreka'} />
                     </div>
                </div>
                
                <div className="flex justify-between items-center pt-4 border-t">
                    <div className="text-lg font-bold">Tổng tiền khách trả: {formatCurrency(totalAmount)}</div>
                    <div className="flex gap-3">
                        <button type="button" onClick={onClose} className="py-2 px-5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300">Hủy</button>
                        <button type="submit" className="py-2 px-5 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Lưu</button>
                    </div>
                </div>
            </form>
        </CustomModal>
    );
};
